import { showNotification } from "services/api/snackbar";
import { Errors, Fields } from "./types";
import { validateYup } from "./validation";

export interface ValidationFeedbackOptions {
  /** Ograničava traženje polja na dio DOM-a (npr. aktivni korak wizarda). */
  container?: HTMLElement | null;
  /** Ljudski naziv polja po ključu sheme; bez njega se ispisuje poruka iz sheme. */
  labels?: Record<string, string>;
  /** Bez poruke — samo skrol na polje. */
  silent?: boolean;
  /** Bez skrola — samo poruka. */
  noScroll?: boolean;
}

/**
 * Traži polje u DOM-u po ključu iz sheme.
 *
 * Kontrole koje renderiraju `<input>` (TextField, TextArea, NumberField, DatePicker, Checkbox)
 * nalaze se po `name`; one koje ga nemaju (Dropdown) po `data-field`. Ugniježđene Yup putanje
 * ("location.contactId") padaju natrag na zadnji segment, jer se polja tako i imenuju.
 */
const findField = (key: string, container?: HTMLElement | null): HTMLElement | null => {
  const root: ParentNode = container ?? document;
  const keys = key.includes(".") ? [key, key.split(".").pop() as string] : [key];

  for (const candidate of keys) {
    const field = root.querySelector<HTMLElement>(
      `[name="${candidate}"], [data-field="${candidate}"]`
    );
    if (field) return field;
  }

  return null;
};

/**
 * Javlja korisniku zašto forma ne prolazi.
 *
 * Polje s greškom zna biti izvan vidljivog dijela — korisnik pritisne gumb i naizgled se ne
 * dogodi ništa. Zato poruka ide u snackbar (vidi se bez obzira na skrol), a forma skoči na
 * prvo polje s greškom.
 */
export const reportValidationErrors = (
  errors: Errors,
  options: ValidationFeedbackOptions = {}
): void => {
  const { container, labels, silent, noScroll } = options;

  const keys = Object.keys(errors).filter((key) => errors[key]);
  if (!keys.length) return;

  if (!silent) {
    const messages = keys.map((key) => labels?.[key] ?? errors[key]);
    showNotification(messages.join(" · "), { variant: "warning" });
  }

  if (noScroll) return;

  const field = findField(keys[0], container);
  if (!field) return;

  field.scrollIntoView({ behavior: "smooth", block: "center" });
  field.focus({ preventScroll: true });
};

/**
 * `validateYup` + poruka i skrol na prvo polje s greškom. Koristi se svugdje gdje forma ne stane
 * na ekran, pa tiha validacija izgleda kao da gumb ne radi.
 */
export const validateYupWithFeedback = (
  fields: Fields,
  schema: any,
  setErrors: (errors: Errors) => void,
  options?: ValidationFeedbackOptions
): boolean => {
  let captured: Errors = {};

  const isValid = validateYup(fields, schema, (errors) => {
    captured = errors;
    setErrors(errors);
  });

  if (!isValid) reportValidationErrors(captured, options);

  return isValid;
};
